// Copyright (c) StrangeBee 2023

import { ICONS } from '@Constants/iconsApp';
import { useLocalMessage } from '@Helpers/locale';
import { isEmptyObject } from '@Helpers/object';
import { ManageFiltersOutput } from '@Hooks/useFilters';
import { THButton } from '@Molecules/Inputs/THButton/THButton';
import { FilterFormatter, FilterPreview } from '@Organisms/Filter/FilterPreview/FilterPreview';
import React, { ReactElement } from 'react';
import { DescribeFieldOutput } from 'thehive-sdk';

import './Footer.scss';

export const computeKeyFilter = ({ field = '', operator = '' }, index = 0): string => field + operator + index;

export interface FooterProps {
    filterConfig: ManageFiltersOutput;
    formatter?: FilterFormatter;
    describeField?: DescribeFieldOutput;
    canApplyFilter?: boolean;
}

export const Footer = ({
    filterConfig,
    formatter,
    describeField,
    canApplyFilter = true,
}: FooterProps): ReactElement => {
    const i = useLocalMessage();

    const { editFilters, tempFilters, removeFilter, removeTempFilter, addTempFilter, applyFilters, clearFilters } =
        filterConfig;

    return (
        <div className="th-filter__bottom">
            <div className="th-filter__bottom__preview">
                {tempFilters.map((dataFilter, index) => {
                    return !isEmptyObject(dataFilter) ? (
                        <FilterPreview
                            key={computeKeyFilter(dataFilter, index)}
                            data={dataFilter}
                            describeField={describeField}
                            formatter={formatter}
                            onClose={(): void => {
                                removeTempFilter(index);
                                removeFilter(index);
                            }}
                        />
                    ) : null;
                })}
            </div>
            <div className="th-filter-footer__actions">
                {editFilters && (
                    <>
                        <THButton
                            type="link"
                            icon={<ICONS.PlusIcon />}
                            onClick={(): void => addTempFilter({})}
                            className="th-filter__bottom__button"
                        >
                            {i('filter.footer.add')}
                        </THButton>
                        {canApplyFilter && (
                            <THButton
                                type="link"
                                icon={<ICONS.CheckIcon />}
                                onClick={applyFilters}
                                className="th-filter__bottom__button"
                            >
                                {i('filter.footer.apply')}
                            </THButton>
                        )}
                    </>
                )}

                <THButton
                    type="link"
                    icon={<ICONS.DeleteIcon />}
                    onClick={clearFilters}
                    className="th-filter__bottom__button"
                >
                    {i('filter.footer.clear')}
                </THButton>
            </div>
        </div>
    );
};

Footer.displayName = 'Footer';
